import React, { useState, useEffect } from 'react';
import { Play, Mail, Phone, Instagram, Linkedin, Video, X, Settings } from 'lucide-react';
import AdminPanel from './AdminPanel';
import LoginModal from './LoginModal';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';
import '../styles/portfolio.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Portfolio = () => {
  const [scrolled, setScrolled] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Settings state
  const [heroSettings, setHeroSettings] = useState({
    background_type: 'image',
    background_url: '',
    title: 'Pedro Mota Visuals',
    subtitle: 'Video & Photography'
  });
  
  const [profileSettings, setProfileSettings] = useState({
    name: 'Pedro Mota',
    role: 'Audiovisual Director & Cinematographer',
    description: 'Com 15 anos de experiência na área audiovisual e licenciatura especializada, dedico-me a criar narrativas visuais que transcendem o comum.',
    profile_image_url: null,
    email: 'motapedro001@gmail.com',
    phone: '+351 966 656 362',
    instagram: '@pedromota.visuals',
    vimeo: 'pedromota',
    linkedin: 'pedro-mota-visuals'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchVideos();
    fetchSettings();
    checkAuthentication();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setHeroSettings(response.data.hero);
      setProfileSettings(response.data.profile);
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettingsLoaded(true);
    }
  };

  const checkAuthentication = () => {
    const token = localStorage.getItem('admin_token');
    setIsAuthenticated(!!token);
  };

  const handleAdminClick = () => {
    if (isAuthenticated) {
      setShowAdmin(true);
    } else {
      setShowLogin(true);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setShowLogin(false);
    setShowAdmin(true);
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    fetchVideos();
    fetchSettings();
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [selectedVideo]);

  return (
    <div className="portfolio-container">
      {/* Admin Access Button */}
      <button 
        className="admin-access-btn"
        onClick={handleAdminClick}
        title="Painel Admin"
      >
        <Settings size={20} />
      </button>

      {/* Navigation */}
      <nav className={`nav-portfolio ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-content">
          <a href="#home" className="nav-logo" onClick={() => scrollToSection('home')}>
            Pedro Mota Visuals
          </a>
          <ul className="nav-links">
            <li>
              <a href="#videos" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('videos'); }}>
                Portfólio
              </a>
            </li>
            <li>
              <a href="#about" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>
                Sobre
              </a>
            </li>
            <li>
              <a href="#contact" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>
                Contacto
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        {settingsLoaded && heroSettings.background_url ? (
          <>
            {heroSettings.background_type === 'video' ? (
              <video 
                src={`${BACKEND_URL}${heroSettings.background_url}`}
                className="hero-background"
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <img 
                src={heroSettings.background_url.startsWith('http') ? heroSettings.background_url : `${BACKEND_URL}${heroSettings.background_url}`}
                alt="Hero" 
                className="hero-background"
              />
            )}
          </>
        ) : (
          <div className="hero-background" style={{backgroundColor: '#000'}}></div>
        )}
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">{heroSettings.title}</h1>
          <p className="hero-subtitle">{heroSettings.subtitle}</p>
          <button className="hero-cta" onClick={() => scrollToSection('videos')}>
            Ver Portfólio
          </button>
        </div>
      </section>

      {/* Videos Section */}
      <section id="videos" className="section">
        <h2 className="section-title">Portfólio</h2>
        <p className="section-subtitle">
          Narrativas visuais que capturam a essência de cada momento
        </p>
        {loading ? (
          <div className="loading-state">Carregando...</div>
        ) : videos.length === 0 ? (
          <div className="empty-state">
            <p>Nenhum vídeo adicionado ainda.</p>
            <button onClick={handleAdminClick} className="hero-cta">
              Adicionar Vídeos
            </button>
          </div>
        ) : (
          <div className="video-grid">
            {videos.map((video) => (
              <div 
                key={video.id} 
                className="video-card"
                onClick={() => setSelectedVideo(video)}
              >
                <img 
                  src={`${BACKEND_URL}${video.thumbnail_url}`}
                  alt={video.title} 
                  className="video-thumbnail"
                />
                <div className="play-button">
                  <Play />
                </div>
                <div className="video-overlay">
                  <h3 className="video-title">{video.title}</h3>
                  <div className="video-meta">
                    <span className="video-category">{video.category}</span>
                    <span>{video.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <h2 className="section-title">Sobre</h2>
        <div className="about-content">
          {profileSettings.profile_image_url && (
            <img 
              src={`${BACKEND_URL}${profileSettings.profile_image_url}`}
              alt={profileSettings.name}
              className="about-image"
            />
          )}
          <h3 className="about-name">{profileSettings.name}</h3>
          <p className="about-role">{profileSettings.role}</p>
          <p className="about-description">{profileSettings.description}</p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <h2 className="section-title">Contacto</h2>
        <div className="contact-content">
          <p className="section-subtitle">
            Vamos trabalhar juntos no seu próximo projeto
          </p>
          <div className="contact-methods">
            <div className="contact-method">
              <Mail className="contact-icon" />
              <p className="contact-label">Email</p>
              <a href={`mailto:${profileSettings.email}`} className="contact-link">
                <p className="contact-value">{profileSettings.email}</p>
              </a>
            </div>
            <div className="contact-method">
              <Phone className="contact-icon" />
              <p className="contact-label">Telemóvel</p>
              <a href={`tel:${profileSettings.phone}`} className="contact-link">
                <p className="contact-value">{profileSettings.phone}</p>
              </a>
            </div>
          </div>
          <div className="social-links">
            {profileSettings.instagram && (
              <a href={`https://instagram.com/${profileSettings.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="social-link">
                <Instagram size={24} />
              </a>
            )}
            {profileSettings.vimeo && (
              <a href={`https://vimeo.com/${profileSettings.vimeo}`} target="_blank" rel="noopener noreferrer" className="social-link">
                <Video size={24} />
              </a>
            )}
            {profileSettings.linkedin && (
              <a href={`https://linkedin.com/in/${profileSettings.linkedin}`} target="_blank" rel="noopener noreferrer" className="social-link">
                <Linkedin size={24} />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p className="footer-text">
          © 2025 Pedro Mota Visuals. Todos os direitos reservados.
        </p>
      </footer>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="modal-overlay" onClick={() => setSelectedVideo(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedVideo(null)}>
              <X size={32} />
            </button>
            <video 
              className="modal-video" 
              controls 
              autoPlay
              src={`${BACKEND_URL}${selectedVideo.video_url}`}
            >
              Your browser does not support the video tag.
            </video>
            <div className="modal-info">
              <h3 className="modal-title">{selectedVideo.title}</h3>
              <div className="modal-meta">
                <span className="video-category">{selectedVideo.category}</span>
                <span>{selectedVideo.duration}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Panel */}
      {showAdmin && (
        <AdminPanel 
          onClose={() => setShowAdmin(false)}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {/* Login Modal */}
      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {/* Toast Notifications */}
      <Toaster />
    </div>
  );
};

export default Portfolio;